import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Table, Input, Button, Space, Tooltip, Empty, Popover } from "antd";
import { Spin, Popconfirm, Select, Card, Row, Col, Checkbox } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import {
  ReloadOutlined,
  HolderOutlined,
  SearchOutlined,
  VerticalAlignBottomOutlined,
  VerticalAlignTopOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { debounce } from "@common/helpers";
import { DELAYS } from "@common/variables";
import type { DataTableProps, DataTableState } from "./types";
import type { ColumnsType } from "antd/es/table";
import styles from "./styles.module.less";

const { Option } = Select;

// Export DataTable types
export type {
  DataTableColumn,
  DataTableAction,
  DataTableProps,
  DataTableState,
} from "./types";

export function DataTable<T = Record<string, unknown>>({
  fetchFn,
  columns,
  rowKey,
  actions = [],
  onCreate,
  searchable = true,
  searchPlaceholder = "Tìm kiếm...",
  filters = [],
  filterCardBordered = true,
  filterRowProps,
  filterColProps,
  defaultPageSize = 20,
  pageSizeOptions = ["10", "20", "50", "100"],
  showPagination = true,
  scrollable = true,
  scrollX,
  scrollY,
  sticky = true,
  bordered = false,
  title,
  emptyText = "Không có dữ liệu",
  loading: externalLoading,
  className,
  headerActions,
  onRowClick,
  onRefresh,
  additionalParams = {},
  storageKey,
}: DataTableProps<T>) {
  const [state, setState] = useState<DataTableState<T>>({
    data: [],
    loading: false,
    error: null,
    pagination: {
      current: 1,
      pageSize: defaultPageSize,
      total: 0,
    },
    searchTerm: "",
    filters: {},
  });

  const getColumnKey = useCallback((col: ColumnsType<T>[0]): string => {
    if (col.key) return String(col.key);
    if ("dataIndex" in col && col.dataIndex) {
      const dataIndex = col.dataIndex;
      return Array.isArray(dataIndex) ? dataIndex.join(".") : String(dataIndex);
    }
    return "";
  }, []);

  // Generate storage key for localStorage
  const tableStorageKey = useMemo(() => {
    if (storageKey) return `datatable-column-widths-${storageKey}`;
    // Generate key from columns
    const columnKeys = columns.map((col) => getColumnKey(col)).join("-");
    return `datatable-column-widths-${columnKeys}`;
  }, [storageKey, columns, getColumnKey]);

  // Load column widths from localStorage on mount
  const loadColumnWidths = useCallback((): Record<string, number> => {
    try {
      const saved = localStorage.getItem(tableStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return typeof parsed === "object" && parsed !== null ? parsed : {};
      }
    } catch (error) {
      console.warn("Failed to load column widths from localStorage:", error);
    }
    return {};
  }, [tableStorageKey]);

  const [visibleColumnKeys, setVisibleColumnKeys] = useState<Set<string>>(
    () => {
      const keys = new Set<string>();
      columns.forEach((col) => {
        const key = getColumnKey(col);
        if (key) keys.add(key);
      });
      return keys;
    }
  );

  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    return columns.map((col) => getColumnKey(col)).filter(Boolean);
  });

  const [settingPopoverOpen, setSettingPopoverOpen] = useState(false);
  const [draggedItemKey, setDraggedItemKey] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  
  // Column resizing state - load from localStorage on mount
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => loadColumnWidths());
  const [isResizing, setIsResizing] = useState(false);
  const [resizeLineX, setResizeLineX] = useState<number | null>(null);
  const [resizeLineTop, setResizeLineTop] = useState<number>(0);
  const [resizeLineHeight, setResizeLineHeight] = useState<number>(55);
  const resizeRef = useRef<{ columnKey: string; startX: number; startWidth: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  // Save column widths to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(tableStorageKey, JSON.stringify(columnWidths));
    } catch (error) {
      console.warn("Failed to save column widths to localStorage:", error);
    }
  }, [columnWidths, tableStorageKey]);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const filtersRef = useRef<Record<string, unknown>>({});
  const additionalParamsRef = useRef<Record<string, unknown>>({});
  const searchTermRef = useRef<string>("");
  const isInitialMount = useRef(true);
  const fetchingRef = useRef(false);
  const lastFetchParamsRef = useRef<string>("");
  const searchFilterKeyRef = useRef<string | null>(null);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((...args: unknown[]) => {
        const value = args[0] as string;
        setDebouncedSearchTerm(value);
      }, DELAYS.DEBOUNCE_SEARCH),
    []
  );

  useEffect(() => {
    filtersRef.current = state.filters;
  }, [state.filters]);

  useEffect(() => {
    additionalParamsRef.current = additionalParams;
  }, [additionalParams]);

  useEffect(() => {
    searchTermRef.current = debouncedSearchTerm;
  }, [debouncedSearchTerm]);

  const fetchData = useCallback(
    async (page = 1, pageSize = defaultPageSize) => {
      const offset = (page - 1) * pageSize;
      const params: Record<string, unknown> = {
        offset,
        limit: pageSize,
        ...additionalParamsRef.current,
      };
      // Handle search from filters (if search filter exists) or from searchable prop
      if (searchFilterKeyRef.current) {
        const searchValue = filtersRef.current[searchFilterKeyRef.current] as
          | string
          | undefined;
        if (searchValue) {
          params.search = searchValue;
        }
      } else if (searchTermRef.current) {
        params.search = searchTermRef.current;
      }

      Object.entries(filtersRef.current).forEach(([key, value]) => {
        // Skip search filter key as it's already handled above
        if (searchFilterKeyRef.current && key === searchFilterKeyRef.current)
          return;
        if (value !== undefined && value !== null && value !== "") {
          params[key] = value;
        }
      });
      const requestKey = JSON.stringify(params);
      if (fetchingRef.current && lastFetchParamsRef.current === requestKey) {
        return;
      }

      fetchingRef.current = true;
      lastFetchParamsRef.current = requestKey;
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response = await fetchFn({
          offset,
          limit: pageSize,
          ...params,
        });

        if (response.code === 0 && response.data?.items) {
          const responseData = response.data;
          console.log(responseData, 'responseData');
          setState((prev) => ({
            ...prev,
            data:responseData.items as T[],
            pagination: {
              current: page,
              pageSize: pageSize,
              total: responseData.total || 0,
            },
            loading: false,
          }));
        } else {
          setState((prev) => ({
            ...prev,
            error: response.message || "Không thể tải dữ liệu",
            data: [],
            loading: false,
          }));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setState((prev) => ({
          ...prev,
          error: "Lỗi khi tải dữ liệu",
          data: [],
          loading: false,
        }));
      } finally {
        fetchingRef.current = false;
      }
    },
    [fetchFn, defaultPageSize]
  );

  const filtersKey = useMemo(
    () => JSON.stringify(state.filters),
    [state.filters]
  );
  const additionalParamsKey = useMemo(
    () => JSON.stringify(additionalParams),
    [additionalParams]
  );

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchData(1, state.pagination.pageSize);
      return;
    }
    fetchData(1, state.pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, filtersKey, additionalParamsKey]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState((prev) => ({ ...prev, searchTerm: value }));
    debouncedSetSearchTerm(value);
  };

  const handleFilterChange = (key: string, value: unknown) => {
    setState((prev) => ({
      ...prev,
      filters: { ...prev.filters, [key]: value },
      pagination: { ...prev.pagination, current: 1 },
    }));
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    if (newPagination.current && newPagination.pageSize) {
      fetchData(newPagination.current, newPagination.pageSize);
    }
  };

  // Check if search is handled by filters
  const searchFilter = useMemo(() => {
    return filters.find((f) => f.type === "search" || f.type === "input");
  }, [filters]);
  const isSearchInFilters = !!searchFilter;

  useEffect(() => {
    searchFilterKeyRef.current = searchFilter?.key || null;
  }, [searchFilter]);

  const handleRefresh = useCallback(() => {
    setState((prev) => {
      fetchData(prev.pagination.current, prev.pagination.pageSize);
      return prev;
    });
    onRefresh?.();
  }, [fetchData, onRefresh]);

  // Handle column visibility toggle
  const handleColumnVisibilityChange = useCallback(
    (key: string, checked: boolean) => {
      setVisibleColumnKeys((prev) => {
        const newSet = new Set(prev);
        if (checked) {
          newSet.add(key);
        } else {
          newSet.delete(key);
        }
        return newSet;
      });
    },
    []
  );

  const handleColumnMove = useCallback(
    (key: string, direction: "up" | "down") => {
      setColumnOrder((prev) => {
        const newOrder = [...prev];
        const currentIndex = newOrder.indexOf(key);
        if (currentIndex === -1) return prev;

        if (direction === "up" && currentIndex > 0) {
          [newOrder[currentIndex], newOrder[currentIndex - 1]] = [
            newOrder[currentIndex - 1],
            newOrder[currentIndex],
          ];
        } else if (direction === "down" && currentIndex < newOrder.length - 1) {
          [newOrder[currentIndex], newOrder[currentIndex + 1]] = [
            newOrder[currentIndex + 1],
            newOrder[currentIndex],
          ];
        }
        return newOrder;
      });
    },
    []
  );

  const allColumnsVisible = useMemo(() => {
    return (
      columnOrder.length > 0 &&
      columnOrder.every((key) => visibleColumnKeys.has(key))
    );
  }, [columnOrder, visibleColumnKeys]);

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        setVisibleColumnKeys(new Set(columnOrder));
      } else {
        setVisibleColumnKeys(new Set());
      }
    },
    [columnOrder]
  );

  const handleResetColumns = useCallback(() => {
    const keys = new Set<string>();
    columns.forEach((col) => {
      const key = getColumnKey(col);
      if (key) keys.add(key);
    });
    setVisibleColumnKeys(keys);
    setColumnOrder(columns.map((col) => getColumnKey(col)).filter(Boolean));
  }, [columns, getColumnKey]);

  const handleDragStart = useCallback((key: string) => {
    setDraggedItemKey(key);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault();
      if (draggedItemKey === null) return;

      setColumnOrder((prev) => {
        const newOrder = [...prev];
        const dragIndex = newOrder.indexOf(draggedItemKey);
        if (dragIndex === -1) return prev;
        if (dragIndex === dropIndex) return prev;
        newOrder.splice(dragIndex, 1);
        const adjustedDropIndex =
          dragIndex < dropIndex ? dropIndex - 1 : dropIndex;
        newOrder.splice(adjustedDropIndex, 0, draggedItemKey);
        return newOrder;
      });

      setDraggedItemKey(null);
      setDragOverIndex(null);
    },
    [draggedItemKey]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedItemKey(null);
    setDragOverIndex(null);
  }, []);

  // Handle column resize
  const handleResizeStart = useCallback((columnKey: string, startX: number, startWidth: number) => {
    resizeRef.current = { columnKey, startX, startWidth };
    setIsResizing(true);
    setResizeLineX(startX);
    
    // Tính toán vị trí top của header
    const tableHeader = document.querySelector('.ant-table-thead');
    if (tableHeader) {
      const rect = tableHeader.getBoundingClientRect();
      setResizeLineTop(rect.top);
      setResizeLineHeight(rect.height);
    }
  }, []);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizeRef.current) return;
    
    // Cancel previous RAF if exists
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }
    
    // Use requestAnimationFrame for smooth updates
    rafRef.current = requestAnimationFrame(() => {
      if (!resizeRef.current) return;
      
      const { columnKey, startX, startWidth } = resizeRef.current;
      const diff = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + diff); // Minimum width 50px
      
      // Update resize line position
      setResizeLineX(e.clientX);
      
      // Batch state updates
      setColumnWidths((prev) => {
        if (prev[columnKey] === newWidth) return prev;
        return {
          ...prev,
          [columnKey]: newWidth,
        };
      });
    });
  }, []);

  const handleResizeEnd = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    resizeRef.current = null;
    setIsResizing(false);
    setResizeLineX(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      const updateHeaderPosition = () => {
        const tableHeader = document.querySelector('.ant-table-thead');
        if (tableHeader) {
          const rect = tableHeader.getBoundingClientRect();
          setResizeLineTop(rect.top);
          setResizeLineHeight(rect.height);
        }
      };
      
      const handleMouseMove = (e: MouseEvent) => {
        e.preventDefault();
        updateHeaderPosition(); // Cập nhật vị trí header khi scroll
        if (resizeRef.current) {
          handleResizeMove(e);
        }
      };
      const handleMouseUp = () => {
        handleResizeEnd();
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.body.style.pointerEvents = "";
      };
      
      // Cập nhật vị trí header khi scroll
      const handleScroll = () => {
        updateHeaderPosition();
      };
      
      // Use passive: false to allow preventDefault
      document.addEventListener("mousemove", handleMouseMove, { passive: false });
      document.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("scroll", handleScroll, true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.body.style.pointerEvents = "auto";
      
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        window.removeEventListener("scroll", handleScroll, true);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.body.style.pointerEvents = "";
        if (rafRef.current !== null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  const enhancedColumns = useMemo(() => {
    const columnMap = new Map<string, ColumnsType<T>[0]>();
    columns.forEach((col) => {
      const key = getColumnKey(col);
      if (key) columnMap.set(key, col);
    });

    const orderedColumns = columnOrder
      .filter((key) => visibleColumnKeys.has(key))
      .map((key) => columnMap.get(key))
      .filter((col): col is ColumnsType<T>[0] => col !== undefined);

    columns.forEach((col) => {
      const key = getColumnKey(col);
      if (key && !columnOrder.includes(key) && visibleColumnKeys.has(key)) {
        orderedColumns.push(col);
      }
    });

    const cols = [...orderedColumns].map((col) => {
      const key = getColumnKey(col);
      const savedWidth = columnWidths[key];
      const currentWidth = savedWidth || col.width || (typeof col.width === 'number' ? col.width : undefined);
      
      return {
        ...col,
        key,
        width: currentWidth,
        onHeaderCell: () => ({
          onMouseDown: (e: React.MouseEvent) => {
            const th = e.currentTarget as HTMLElement;
            const rect = th.getBoundingClientRect();
            // Check if click is within 10px of right edge
            const isNearRightEdge = e.clientX >= rect.right - 10 && e.clientX <= rect.right + 5;
            
            if (isNearRightEdge) {
              e.preventDefault();
              e.stopPropagation();
              const width = typeof currentWidth === 'number' ? currentWidth : (th.offsetWidth || 150);
              handleResizeStart(key, e.clientX, width);
            }
          },
          onMouseMove: (e: React.MouseEvent) => {
            const th = e.currentTarget as HTMLElement;
            const rect = th.getBoundingClientRect();
            const isNearRightEdge = e.clientX >= rect.right - 10 && e.clientX <= rect.right + 5;
            if (isNearRightEdge && !isResizing) {
              th.style.cursor = 'col-resize';
            } else if (!isResizing) {
              th.style.cursor = '';
            }
          },
          onMouseLeave: () => {
            if (!isResizing) {
              const th = document.activeElement as HTMLElement;
              if (th) th.style.cursor = '';
            }
          },
          style: {
            position: 'relative',
            userSelect: 'none',
          } as React.CSSProperties,
        }),
      };
    });
    
    if (actions.length > 0) {
      cols.push({
        title: "Hành động",
        key: "action",
        width: 100,
        align: "center",
        fixed: "right" as const,
        onHeaderCell: () => ({
          onMouseDown: () => {
            // No resize for action column
          },
          onMouseMove: () => {
            // No resize for action column
          },
          onMouseLeave: () => {
            // No resize for action column
          },
          style: {
            position: 'relative',
            userSelect: 'none',
          } as React.CSSProperties,
        }),
        render: (_: unknown, record: T) => (
          <Space size="small">
            {actions.map((action) => {
              if (action.visible && !action.visible(record)) {
                return null;
              }
              const button = (
                <Tooltip title={action.tooltip || action.label}>
                  <Button
                    icon={action.icon}
                    size="small"
                    danger={action.danger}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!action.confirm) {
                        action.onClick(record);
                      }
                    }}
                  />
                </Tooltip>
              );
              if (action.confirm) {
                return (
                  <Popconfirm
                    key={action.key}
                    title={action.confirm.title}
                    description={action.confirm.description}
                    onConfirm={() => action.onClick(record)}
                    okText={action.confirm.okText || "Xác nhận"}
                    cancelText={action.confirm.cancelText || "Hủy"}
                    okButtonProps={{ danger: action.danger }}
                  >
                    {button}
                  </Popconfirm>
                );
              }
              return <span key={action.key}>{button}</span>;
            })}
          </Space>
        ),
      });
    }
    return cols;
  }, [columns, actions, columnOrder, visibleColumnKeys, getColumnKey, columnWidths, handleResizeStart, isResizing]);

  const isLoading =
    externalLoading !== undefined ? externalLoading : state.loading;

  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const [tableBodyHeight, setTableBodyHeight] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    const calculateHeight = () => {
      if (contentWrapperRef.current) {
        const viewportHeight = window.innerHeight;
        const contentWrapperRect =
          contentWrapperRef.current.getBoundingClientRect();
        const header = contentWrapperRef.current.querySelector(
          `.${styles.header}`
        );
        let usedHeight = 16;
        if (header) {
          usedHeight += (header as HTMLElement).offsetHeight + 16; // Header + margin-bottom
        }

        const topOffset = contentWrapperRect.top;
        const paginationHeight = 70;
        const bottomPadding = 16;
        const availableHeight =
          viewportHeight -
          topOffset -
          usedHeight -
          paginationHeight -
          bottomPadding;
        setTableBodyHeight(availableHeight - 64);
      }
    };
    const timeoutId = setTimeout(calculateHeight, 150);
    window.addEventListener("resize", calculateHeight);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", calculateHeight);
    };
  }, [state.data.length, filters.length]);
  const finalScrollY = scrollable ? scrollY || tableBodyHeight : undefined;

  return (
    <div className={`${styles.dataTable} ${className || ""}`}>
      {((searchable && !isSearchInFilters) || filters.length > 0) && (
        <Card
          variant="borderless"
          className={`${styles.filterCard} ${
            filterCardBordered ? styles.bordered : styles.noBorder
          }`}
          size="small"
        >
          <Row {...(filterRowProps || { gutter: [16, 16] })}>
            {searchable && !isSearchInFilters && (
              <Col {...(filterColProps || { xs: 24, sm: 12, md: 8, lg: 6 })}>
                <Input
                  placeholder={searchPlaceholder}
                  prefix={<SearchOutlined />}
                  value={state.searchTerm}
                  onChange={handleSearchChange}
                  allowClear
                  className={styles.filterInput}
                />
              </Col>
            )}
            {filters.map((filter) => {
              const colProps = filter.colProps ||
                filterColProps || { xs: 24, sm: 12, md: 8, lg: 6 };
              return (
                <Col key={filter.key} {...colProps}>
                  {filter.type === "input" || filter.type === "search" ? (
                    <Input
                      placeholder={filter.placeholder || filter.label}
                      prefix={
                        filter.type === "search" ? (
                          <SearchOutlined />
                        ) : undefined
                      }
                      value={(state.filters[filter.key] as string) || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleFilterChange(filter.key, value);
                        filter.onChange?.(value);
                      }}
                      allowClear
                      className={styles.filterInput}
                    />
                  ) : (
                    <Select
                      placeholder={filter.placeholder || filter.label}
                      allowClear
                      value={state.filters[filter.key] as string | undefined}
                      onChange={(value) => {
                        handleFilterChange(filter.key, value);
                        filter.onChange?.(value);
                      }}
                      className={styles.filterSelect}
                      style={{ width: "100%" }}
                    >
                      {filter.options?.map((option) => (
                        <Option key={String(option.value)} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  )}
                </Col>
              );
            })}
          </Row>
        </Card>
      )}
      <div className={styles.contentWrapper} ref={contentWrapperRef}>
        {(title || onCreate || headerActions) && (
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              {title && <h3 className={styles.title}>{title}</h3>}
            </div>
            <div className={styles.headerRight}>
              {headerActions}
              <Tooltip title="Làm mới">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={isLoading}
                  type="text"
                  className={styles.refreshButton}
                />
              </Tooltip>
              <Popover
                content={
                  <div className={styles.columnSettings}>
                    <div className={styles.columnSettingsHeader}>
                      <Checkbox
                        checked={allColumnsVisible}
                        indeterminate={
                          !allColumnsVisible && visibleColumnKeys.size > 0
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      >
                        <span className={styles.columnSettingsHeaderText}>
                          Cột hiển thị
                        </span>
                      </Checkbox>
                      <Button
                        type="link"
                        size="small"
                        onClick={handleResetColumns}
                        className={styles.resetButton}
                      >
                        Làm lại
                      </Button>
                    </div>
                    <div className={styles.columnSettingsList}>
                      {columnOrder.map((key, index) => {
                        const col = columns.find(
                          (c) => getColumnKey(c) === key
                        );
                        if (!col) return null;
                        const title = (col.title as string) || key;
                        const isVisible = visibleColumnKeys.has(key);
                        const isDragging = draggedItemKey === key;
                        const isDragOver =
                          dragOverIndex === index && draggedItemKey !== key;
                        return (
                          <div
                            key={key}
                            className={`${styles.columnSettingItem} ${
                              isDragging ? styles.dragging : ""
                            } ${isDragOver ? styles.dragOver : ""}`}
                            draggable
                            onDragStart={() => handleDragStart(key)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <HolderOutlined className={styles.dragHandle} />
                            <Checkbox
                              checked={isVisible}
                              onChange={(e) =>
                                handleColumnVisibilityChange(
                                  key,
                                  e.target.checked
                                )
                              }
                            >
                              {title}
                            </Checkbox>
                            <Space size={0} className={styles.columnActions}>
                              <Button
                                type="text"
                                size="small"
                                icon={<VerticalAlignTopOutlined />}
                                onClick={() => handleColumnMove(key, "up")}
                                disabled={index === 0}
                                title="Di chuyển lên"
                              />
                              <Button
                                type="text"
                                size="small"
                                icon={<VerticalAlignBottomOutlined />}
                                onClick={() => handleColumnMove(key, "down")}
                                disabled={index === columnOrder.length - 1}
                                title="Di chuyển xuống"
                              />
                            </Space>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                }
                trigger="click"
                open={settingPopoverOpen}
                onOpenChange={setSettingPopoverOpen}
                placement="bottomRight"
              >
                <Tooltip title="Cài đặt cột">
                  <Button
                    icon={<SettingOutlined />}
                    type="text"
                    className={styles.settingButton}
                  />
                </Tooltip>
              </Popover>
            </div>
          </div>
        )}

        <div className={styles.tableWrapper}>
          {isLoading && state.data.length === 0 ? (
            <div className={styles.loading}>
              <Spin size="large" />
              <span>Đang tải dữ liệu...</span>
            </div>
          ) : state.error ? (
            <div className={styles.error}>{state.error}</div>
          ) : (
            <div className={styles.tableWrapper}>
              {isResizing && resizeLineX !== null && (
                <div
                  className={styles.resizeLine}
                  style={{ 
                    left: `${resizeLineX}px`,
                    top: `${resizeLineTop}px`,
                    height: `${resizeLineHeight}px`,
                  }}
                />
              )}
              <Table<T>
                columns={enhancedColumns}
                dataSource={state.data}
                rowKey={rowKey}
                loading={isLoading && state.data.length > 0}
              pagination={
                showPagination
                  ? {
                      current: state.pagination.current,
                      pageSize: state.pagination.pageSize,
                      total: state.pagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) => (
                        <span className={styles.paginationTotal}>
                          Hiển thị {range[0]} - {range[1]} trong tổng số{" "}
                          <strong>{total}</strong> mục
                        </span>
                      ),
                      pageSizeOptions,
                      size: "default",
                    }
                  : false
              }
              onChange={handleTableChange}
              scroll={
                scrollable
                  ? {
                      x: scrollX || "max-content",
                      y: finalScrollY,
                    }
                  : undefined
              }
              sticky={sticky ? { offsetHeader: 0 } : undefined}
              onRow={
                onRowClick
                  ? (record) => ({
                      onClick: () => onRowClick(record),
                      style: { cursor: "pointer" },
                    })
                  : undefined
              }
              locale={{
                emptyText: (
                  <Empty
                    description={emptyText}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
              className={`${styles.table} ${
                bordered ? styles.borderedTable : ""
              }`}
            />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Export DataTable as default
export default DataTable;
